import React, { createContext, useCallback, useMemo, useReducer } from 'react';

type State = {
  height: number;
};

type Action = { type: 'setHeight'; payload: number };

const navbarReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setHeight': {
      return {
        ...state,
        height: action.payload,
      };
    }
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

type NavbarContextValue = {
  navbarHeight: number;
  setNavbarHeight: (arg0: number) => void;
};

const NavbarContext = createContext<NavbarContextValue | undefined>(undefined);

type NavbarProviderProps = {
  children: React.ReactNode;
};

const DEFAULT_NAVBAR_HEIGHT = 73;

const initialState = {
  height: DEFAULT_NAVBAR_HEIGHT,
};

const NavbarProvider = ({ children }: NavbarProviderProps) => {
  const [state, dispatch] = useReducer(navbarReducer, initialState);

  const setNavbarHeight = useCallback(
    (height: number) => {
      if (state.height !== height) {
        dispatch({ type: 'setHeight', payload: height });
      }
    },
    [state.height]
  );

  const value = useMemo(() => ({ navbarHeight: state.height, setNavbarHeight }), [state, setNavbarHeight]);

  return <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>;
};

const useNavbarState = () => {
  const context = React.useContext(NavbarContext);

  if (context === undefined) {
    throw new Error('useNavbarState must be used within a NavbarProvider');
  }

  return context;
};

export { NavbarProvider, useNavbarState };
